import React, { useState } from "react";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import LmsTable from "../ui/LmsTable";
import { useParseRouteActions, useFetchData } from "../../../helpers/GlobalHelper";
import { IDefaultPagesProps } from "../../interfaces/Lms";
import { guid } from "../../../Helpers";
import { GlobalConstants } from "../../constants/GlobalConstants";

const DetailsTable = ({ id = "" }) => {
  const actionsButtons = [
    { action: "add", path: `loanbudgettemplates/adddetail?templateId=${id}` },
    { action: "edit", path: "loanbudgettemplates/editdetail" },
    "remove",
    "export",
    {
      action: "submitselected",
      text: "Toggle Checked Mandatory",
      submitSuffix: "budget/templates/toggle-mandatory",
      method: "PUT",
      refreshDataOnSuccess: true,
      icon: GlobalConstants.Icons.CARET_UP
    }
  ];

  return <LmsTable
    actionButtons={actionsButtons as any}
    apiSuffix={`budget/templates/${id}/details`}
    deleteSuffix="budget/templates/categories"
    onClickPath="loanbudgettemplates/editdetail"
    title="Details"
  />;
};

const EditCategoryDetailForm = ({ id }) => {
  const [{ data, isLoading, error }] = useFetchData(`budget/templates/category/${id}`);
  const myForm = new BCFormController(data, { submitSuffix: "budget/templates/category", blankWidth: 6 });
  myForm.addCenteredGroup([myForm.getDropdownField("Category", "budget/categories", { name: "loanBudgetCategoryId" })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Is Mandatory")]);
  const actions = ["save", "save-close", "delete", "close"];
  return <LmsFormGenerator myForm={myForm} actions={actions} isLoading={isLoading} error={error} />;
};

const BuiltInCategoryTable = ({ loanBudgetTemplateObjectUid, data }) => {
  const [{ data: currentCategoryData = []}] = useFetchData(loanBudgetTemplateObjectUid ? `budget/templates/selected-categories/${loanBudgetTemplateObjectUid}` : "");
  return <LmsTable
    data={!data ? currentCategoryData : currentCategoryData.length > data.length ? currentCategoryData : data}
    displayOnlyProps={["constructionPhase", "category", "sortOrder"]}
    columnLabelsDictionary={{ sortOrder: "Budget Category Sequence" }}
    formatDataFunc={(data) => data.sort((a, b) => a.sortOrder - b.sortOrder)}
    compact
    isDisabled
  />;
};

const AddNewCategoryTable = ({ setIsAddFormModalOpen = undefined as any, budgetTemplateFormOptions = undefined as any, templateId = "" }) => {
  const loanBudgetTemplateObjectUid = templateId || budgetTemplateFormOptions?.metaData?.loanBudgetTemplateObjectUid || guid();
  if (budgetTemplateFormOptions && !budgetTemplateFormOptions?.metaData?.loanBudgetTemplateObjectUid) {
    budgetTemplateFormOptions.setMetaData({ ...budgetTemplateFormOptions.metaData, loanBudgetTemplateObjectUid });
  }

  const categoriesListSuffix = `budget/templates/categories-for-add-mode/${loanBudgetTemplateObjectUid}`;
  const actionButtons: any = [
    {
      action: "submitselected",
      text: "Add Selected",
      icon: GlobalConstants.Icons.ADD,
      submitSuffix: categoriesListSuffix,
      refreshDataOnSuccess: true,
      onSuccessFunc: () => budgetTemplateFormOptions?.reRenderFormField("selectedCategoriesTable")
    }
  ];

  if (setIsAddFormModalOpen) {
    actionButtons.push({
      action: "close",
      handleClick: () => setIsAddFormModalOpen(false)
    });
  }
  else {
    actionButtons.push("close");
  }

  return <LmsTable
    apiSuffix={categoriesListSuffix}
    actionButtons={actionButtons}
    addSelectedSuffix="budget/templates/categories-for-add-mode"
    clearSuffixesOnSuccess={[`budget/templates/selected-categories/${loanBudgetTemplateObjectUid}`]}
  />;
};

const AddEditTemplateNewCategoryTable = ({ setIsAddFormModalOpen = undefined as any, budgetTemplateFormOptions = undefined as any, templateId = "" }) => {
  const loanBudgetTemplateObjectUid = templateId || budgetTemplateFormOptions?.metaData?.loanBudgetTemplateObjectUid || guid();
  if (budgetTemplateFormOptions && !budgetTemplateFormOptions?.metaData?.loanBudgetTemplateObjectUid) {
    budgetTemplateFormOptions.setMetaData({ ...budgetTemplateFormOptions.metaData, loanBudgetTemplateObjectUid });
  }

  const categoriesListSuffix = `budget/templates/categories-for-add-mode/${loanBudgetTemplateObjectUid}`;
  const actionButtons: any = [
    {
      action: "submitselected",
      text: "Add Selected",
      icon: GlobalConstants.Icons.ADD,
      submitSuffix: `budget/templates/category/${loanBudgetTemplateObjectUid}`,
      clearSuffixes: [`budget/templates/${loanBudgetTemplateObjectUid}/details`]
    }
  ];

  if (setIsAddFormModalOpen) {
    actionButtons.push({
      action: "close",
      handleClick: () => setIsAddFormModalOpen(false)
    });
  }
  else {
    actionButtons.push("close");
  }

  return <LmsTable
    apiSuffix={categoriesListSuffix}
    actionButtons={actionButtons}
    addSelectedSuffix="budget/templates/categories-for-add-mode"
    clearSuffixesOnSuccess={[`budget/templates/selected-categories/${loanBudgetTemplateObjectUid}`]}
  />;
};

const addBudgetTemplateFormFields = (myForm, isAdd = false, isCopy = false) => {
  if (isCopy) {
    myForm.addCenteredGroup([myForm.getDisplayField("Template being Copied", { name: "originalName" })], { blankWidth: 6 });
  }

  myForm.addCenteredGroup([myForm.getInputField("Template Name", { name: "name", required: true })], { blankWidth: 6 });
  myForm.addCenteredGroup([myForm.getDropdownField("Template Owner", "orgs/list-orgs", { name: "ownerOrganizationId", required: true })], { blankWidth: 6 });
  if (!isCopy) {
    myForm.addCenteredGroup([myForm.getCheckboxField("Active", { name: "isActive" })], { blankWidth: 6 });
  }
  
  if (isAdd) {
    myForm.addCenteredGroup([myForm.getButtonField("Category", undefined, {
      modalContent: (setIsAddFormModalOpen, options) => (
        <AddNewCategoryTable setIsAddFormModalOpen={setIsAddFormModalOpen} budgetTemplateFormOptions={options} />
      ),
      modalSize: "fullscreen",
      fullWidth: true
    })], { blankWidth: 6 });
    myForm.addDividerField({ dividerIsHidden: true });
    myForm.addCenteredGroup([myForm.getComponent((options) => {
      return <BuiltInCategoryTable 
        loanBudgetTemplateObjectUid={options?.metaData?.loanBudgetTemplateObjectUid} 
        data={myForm.values?.categoryData ? myForm.values?.categoryData : []}
      />;
    }, { name: "selectedCategoriesTable" })], { blankWidth: 4 });
  }

  return myForm as BCFormController;
};

const EditBudgetTemplateForm = ({ id }) => {
  const getTemplateSuffix = `budget/templates/${id}`;
  const [{ data = {}, isLoading, error }] = useFetchData(id ? getTemplateSuffix : "");
  const myForm = addBudgetTemplateFormFields(new BCFormController(data, { submitSuffix: "budget/templates", clearSuffixesOnSubmit: [getTemplateSuffix] }));
  const formActions = ["save", "save-close", "delete", "close"];

  return <>
    <LmsFormGenerator
      myForm={myForm}
      actions={formActions}
      isLoading={isLoading}
      error={error}
    />
    <DetailsTable id={id} />
  </>;
};

const AddBudgetTemplateForm = ({ id = "" }) => {
  const [{ data, isLoading, error }] = useFetchData(id ? `budget/templates/${id}` : "");
  const submitSuffix = (options) => {
    const loanBudgetTemplateObjectUid = options?.metaData?.loanBudgetTemplateObjectUid || guid();
    return `budget/templates/${loanBudgetTemplateObjectUid}`;
  };

  const myForm = addBudgetTemplateFormFields(new BCFormController({ isActive: true, originalName: data?.name }, { values: { copyId: data?.id }, excludeFromSubmit: ["copyId"], submitSuffix, saveAndContinueSuffix: "loanbudgettemplates" }), true);
  const formActions = ["save-continue", "save-close", "close"];
  return <LmsFormGenerator myForm={myForm} actions={formActions} isLoading={isLoading} error={error} />;
};

const CopyBudgetTemplateForm = ({ id = "" }) => {
  const [loanBudgetTemplateObjectUid] = useState(guid());
  const [{ data, isLoading, error }] = useFetchData(id ? `budget/templates/${id}` : "");
  const [{ data: originalLoanBudgetTemplateObjectUid }] = useFetchData(data?.id ? `budget/copy-template/${id}` : "", {
    method: "POST",
    skipCache: true
  });

  const [{ data: originalCategoriesData = [] }] = useFetchData(originalLoanBudgetTemplateObjectUid ? `budget/templates/selected-categories/${originalLoanBudgetTemplateObjectUid}` : "");
  const originalCategoriesIds = originalCategoriesData.map(category => {
    return category.id;
  });

  const [{ data: addedCategoriesUid }] = useFetchData(originalCategoriesIds.length > 0 ? `budget/templates/categories-for-add-mode/${loanBudgetTemplateObjectUid}` : "", {
    method: "POST",
    payloadData: originalCategoriesIds,
    skipCache: true
  });

  const [{ data: addedCategoriesData }] = useFetchData(addedCategoriesUid ? `budget/templates/categories-for-add-mode/${loanBudgetTemplateObjectUid}` : "");
  const [{ data: selectedCategoriesData  }] = useFetchData(addedCategoriesData ? `budget/templates/selected-categories/${loanBudgetTemplateObjectUid}` : "");
  const submitSuffix = (options) => {
    const loanBudgetTemplateObjectUid = options?.metaData?.loanBudgetTemplateObjectUid || guid();
    return `budget/templates/${loanBudgetTemplateObjectUid}`;
  };

  const [{ data: categoryData }] = useFetchData(selectedCategoriesData ? `budget/templates/selected-categories/${loanBudgetTemplateObjectUid}` : "");
  const myForm = addBudgetTemplateFormFields(new BCFormController({ originalName: data?.name }, { values: { categoryData: categoryData }, metaData: { loanBudgetTemplateObjectUid: loanBudgetTemplateObjectUid }, excludeFromSubmit: ["copyId"], submitSuffix, saveAndContinueSuffix: "loanbudgettemplates" }), true, true);
  const formActions = ["save-continue", "save-close", "close"];
  return <LmsFormGenerator myForm={myForm} actions={formActions} isLoading={isLoading} error={error} />;
};

const LmsLoanBudgetTemplatesTable = () => {
  const actionButtons = [
    "add",
    "copy",
    "edit",
    "remove",
    "export",
    "activate",
    "deactivate"
  ];

  return <LmsTable
    columnLabelsDictionary={{ name: "Template Name", isActive: "Active" }}
    actionButtons={actionButtons as any}
    apiSuffix="budget/templates"
    activateSuffix="budget/activate-templates"
    deActivateSuffix="budget/deactivate-templates"
  />;
};

const LmsLoanBudgetTemplates = ({ ...props }: IDefaultPagesProps) => {
  const { params = {}, history } = props;
  const allowedActions = ["add", "edit"];
  const allowedSubpages = ["adddetail", "editdetail"];
  const { isAdd, isEdit, isCopy, isMainPage, isActionAllowed, id, actionLower, query } = useParseRouteActions(params, allowedActions, allowedSubpages);
  const templateId = query.get("templateId") || "";
  if (!isActionAllowed) {
    history.goBack();
  }

  return <React.Fragment>
    {isMainPage && <LmsLoanBudgetTemplatesTable />}
    {isAdd && <AddBudgetTemplateForm id={id} />}
    {isCopy && <CopyBudgetTemplateForm id={id} />}
    {isEdit && <EditBudgetTemplateForm id={id} />}
    {actionLower === "adddetail" && <AddEditTemplateNewCategoryTable templateId={templateId} />}
    {actionLower === "editdetail" && <EditCategoryDetailForm id={id} />}
  </React.Fragment>;
};

export default LmsLoanBudgetTemplates;